// Indicadores de status, prioridade, tipo, etiqueta e ator.
import clsx from 'clsx';
import {
  BookOpen,
  Bot,
  Bug,
  ChevronsUp,
  ChevronUp,
  Equal,
  ChevronDown,
  FlaskConical,
  Hammer,
  ListChecks,
  Lock,
  Server,
  Shield,
  Sparkles,
  User,
  Wrench,
  Plug,
  Cog,
  Layers,
  Flag,
} from 'lucide-react';
import { PRIORITY_LABELS, STATUS_LABELS, TYPE_LABELS, type Actor, type Priority, type TaskStatus, type TaskType } from '@jynexus/shared';

const STATUS_CLS: Record<TaskStatus, string> = {
  backlog: 'bg-slate-100 text-slate-600',
  todo: 'bg-navy-50 text-navy-700',
  in_progress: 'bg-accent-50 text-accent-700',
  in_review: 'bg-violet-50 text-violet-700',
  blocked: 'bg-red-50 text-red-700',
  done: 'bg-emerald-50 text-emerald-700',
  canceled: 'bg-slate-100 text-slate-400 line-through',
};

export function StatusBadge({ status }: { status: TaskStatus }) {
  return <span className={clsx('inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap', STATUS_CLS[status])}>{STATUS_LABELS[status]}</span>;
}

const PRIORITY_ICON = { critical: ChevronsUp, high: ChevronUp, medium: Equal, low: ChevronDown } as const;
const PRIORITY_CLS: Record<Priority, string> = {
  critical: 'text-red-600',
  high: 'text-accent-600',
  medium: 'text-navy-500',
  low: 'text-slate-400',
};

export function PriorityBadge({ priority, withLabel }: { priority: Priority; withLabel?: boolean }) {
  const Icon = PRIORITY_ICON[priority];
  return (
    <span className={clsx('inline-flex items-center gap-0.5 text-xs font-medium', PRIORITY_CLS[priority])} title={`Prioridade ${PRIORITY_LABELS[priority]}`}>
      <Icon className="size-4" aria-hidden />
      {withLabel ? PRIORITY_LABELS[priority] : <span className="sr-only">Prioridade {PRIORITY_LABELS[priority]}</span>}
    </span>
  );
}

const TYPE_ICON: Record<TaskType, typeof Bug> = {
  epic: Flag,
  story: Layers,
  feature: Sparkles,
  bug: Bug,
  refactor: Wrench,
  task: ListChecks,
  research: FlaskConical,
  documentation: BookOpen,
  infrastructure: Server,
  security: Shield,
};

export function TypeIcon({ type, withLabel }: { type: TaskType; withLabel?: boolean }) {
  const Icon = TYPE_ICON[type];
  return (
    <span className={clsx('inline-flex items-center gap-1 text-xs text-slate-500', type === 'bug' && 'font-medium text-red-600', type === 'security' && 'text-red-700', type === 'story' && 'font-medium text-violet-600', type === 'epic' && 'font-medium text-amber-600')} title={TYPE_LABELS[type]}>
      <Icon className="size-3.5" aria-hidden />
      {withLabel ? TYPE_LABELS[type] : <span className="sr-only">{TYPE_LABELS[type]}</span>}
    </span>
  );
}

export function LabelChip({ name, color, onRemove }: { name: string; color: string; onRemove?: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-1.5 py-px text-[11px] font-medium text-slate-600">
      <span className="size-1.5 rounded-full" style={{ background: color }} aria-hidden />
      {name}
      {onRemove && (
        <button onClick={onRemove} className="-mr-0.5 text-slate-400 hover:text-red-600" aria-label={`Remover etiqueta ${name}`}>
          ×
        </button>
      )}
    </span>
  );
}

export function ActorBadge({ actor, size = 'sm' }: { actor?: Actor | null; size?: 'sm' | 'xs' }) {
  if (!actor) return <span className="text-xs text-slate-400">Sem responsável</span>;
  const Icon = actor.kind === 'agent' ? Bot : actor.kind === 'integration' ? Plug : actor.kind === 'system' ? Cog : User;
  return (
    <span className={clsx('inline-flex items-center gap-1 text-slate-600', size === 'xs' ? 'text-[11px]' : 'text-xs')} title={`${actor.name} (${actor.kind === 'agent' ? 'agente' : actor.kind === 'user' ? 'usuário' : actor.kind})`}>
      <span className={clsx('inline-flex items-center justify-center rounded-full', size === 'xs' ? 'size-4' : 'size-5', actor.kind === 'agent' ? 'bg-accent-100 text-accent-700' : 'bg-navy-100 text-navy-700')}>
        <Icon className={size === 'xs' ? 'size-2.5' : 'size-3'} aria-hidden />
      </span>
      <span className="truncate">{actor.name}</span>
    </span>
  );
}

export function BlockedBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 rounded bg-red-50 px-1 py-px text-[11px] font-semibold text-red-700" title="Bloqueada">
      <Lock className="size-3" aria-hidden /> Bloqueada
    </span>
  );
}

export { Hammer };
